// Exponential-backoff-with-jitter for MQTT reconnect periods.
//
// Why this exists: mqtt.js retries on a FIXED `reconnectPeriod`. When the broker/LB
// accepts the WebSocket but never completes the MQTT handshake, a fixed 5s retry across
// many tabs/realms opens sockets far faster than the (long) connectTimeout reaps them —
// they pile up as ESTABLISHED sockets until Chrome's per-renderer WebSocket cap throws
// ERR_INSUFFICIENT_RESOURCES. Exponential backoff thins the herd; jitter de-syncs realms.

export interface BackoffOpts {
  /** Period used for the first reconnect attempt (attempt 0). */
  baseMs: number;
  /** Upper bound the period never exceeds. */
  capMs: number;
  /** Multiplier applied per consecutive failed attempt. */
  factor: number;
  /** Fraction [0..1] of the period that may be shaved off as random jitter. */
  jitter: number;
  /** Injectable RNG in [0,1) for deterministic tests. Defaults to Math.random. */
  rng?: () => number;
}

/**
 * Period (ms) to wait before reconnect attempt `attempt` (0-based: 0 = first retry).
 * Grows as base * factor^attempt, clamped to cap, then jittered DOWNWARD so the result
 * lands in [full*(1-jitter), full].
 */
export function computeReconnectBackoff(attempt: number, opts: BackoffOpts): number {
  const { baseMs, capMs, factor, jitter } = opts;
  const rng = opts.rng ?? Math.random;
  const full = Math.min(capMs, baseMs * Math.pow(factor, attempt));
  const shaved = full * (1 - jitter * rng());
  return Math.round(shaved);
}
